import { Content, Troubleshooting } from '@amzn/awscat-pattern-library-service-model';

import { reorderById } from '../common/Arrays';
import JSONSerializer from '../common/JSONSerializer';
import { MissingSectionError, SpecialSectionId, updateSpecialSection } from './SectionOperations';

/** Shared serializer — validates shape on hydration, throws on corruption. */
const SERIALIZER = new JSONSerializer();

/**
 * Writes a new troubleshooting array into the content. Returns
 * `undefined` when the section is missing (instead of crashing).
 */
function tryWriteTroubleshootings(
  content: Content,
  next: readonly Troubleshooting[],
): Content | undefined {
  try {
    return updateSpecialSection(content, SpecialSectionId.Troubleshooting, next);
  } catch (e) {
    if (e instanceof MissingSectionError) return undefined;
    throw e;
  }
}

/**
 * Returns the typed Troubleshooting[] for the Pattern's
 * 'troubleshooting' section. Returns `[]` when the section is missing
 * or empty.
 *
 * @throws When the data doesn't match the Troubleshooting schema
 * (indicates corrupted data — not expected to be caught).
 */
export function listTroubleshootings(content: Content): readonly Troubleshooting[] {
  // Locate the section; absent section → empty list.
  const section = content.contentData.contentSections?.find(
    (s) => s.id === SpecialSectionId.Troubleshooting,
  );
  const data = section?.contentData;
  if (data == null) return [];

  // Hydrate via JSONSerializer for shape validation.
  return SERIALIZER.deserializeArray<Troubleshooting>(
    data as unknown as unknown[],
    Troubleshooting,
  );
}

/**
 * Appends `row` to the Pattern's troubleshooting list. Returns
 * `undefined` when the section is missing or a row with the same id
 * already exists.
 */
export function tryAddTroubleshooting(content: Content, row: Troubleshooting): Content | undefined {
  const existing = listTroubleshootings(content);

  // Duplicate guard — may happen via a race.
  if (existing.some((r) => r.id === row.id)) return undefined;

  return tryWriteTroubleshootings(content, [...existing, row]);
}

/**
 * Replaces the troubleshooting row identified by `rowId` with
 * `setter(current)`. Returns `undefined` when the row does not exist
 * or the section is missing.
 */
export function tryUpdateTroubleshooting(
  content: Content,
  rowId: string,
  setter: (row: Troubleshooting) => Troubleshooting,
): Content | undefined {
  const rows = listTroubleshootings(content);
  const index = rows.findIndex((r) => r.id === rowId);
  if (index < 0) return undefined;

  const next = rows.slice();
  // @ts-expect-error TS2345 - strict migration
  next[index] = setter(rows[index]);
  return tryWriteTroubleshootings(content, next);
}

/**
 * Removes the troubleshooting row identified by `rowId`. Returns
 * `undefined` when the row does not exist or the section is missing.
 */
export function tryDeleteTroubleshooting(content: Content, rowId: string): Content | undefined {
  const rows = listTroubleshootings(content);
  if (!rows.some((r) => r.id === rowId)) return undefined;

  return tryWriteTroubleshootings(
    content,
    rows.filter((r) => r.id !== rowId),
  );
}

/**
 * Reorders troubleshooting rows to match `newOrderIds`. Returns
 * `undefined` when the section is missing.
 */
export function tryReorderTroubleshootings(
  content: Content,
  newOrderIds: readonly string[],
): Content | undefined {
  const next = reorderById(listTroubleshootings(content), newOrderIds);
  return tryWriteTroubleshootings(content, next);
}
